import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API_URL } from "../../../config";
import * as XLSX from "xlsx";
import "./AttendanceDashboard.css";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const toDateKey = (dateObj) => {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const parseDateAndTime = (dateKey, timeStr) => {
  if (!timeStr || !dateKey) return null;
  const clean = String(timeStr).trim();
  const parts = clean.split(":").map(Number);
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return null;

  const h = parts[0];
  const m = parts[1];
  const s = parts[2] || 0;

  const dt = new Date(`${dateKey}T00:00:00`);
  dt.setHours(h, m, s, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const formatDuration = (milliseconds) => {
  if (milliseconds == null || milliseconds < 0) return "--";
  const totalSec = Math.floor(milliseconds / 1000);
  const hh = String(Math.floor(totalSec / 3600)).padStart(2, "0");
  const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
  const ss = String(totalSec % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

const formatWorkingHours = (hoursDecimal) => {
  if (!hoursDecimal) return "00:00:00";
  const totalSeconds = Math.round(hoursDecimal * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const formatBreakDuration = (minutes) => {
  if (!minutes) return "00:00:00";
  const totalSeconds = Math.round(minutes * 60);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const formatTime12H = (timeStr) => {
  if (!timeStr) return "--";
  const parts = String(timeStr).split(":");
  if (parts.length < 2) return timeStr;
  let hour = parseInt(parts[0], 10);
  if (isNaN(hour)) return timeStr;
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${parts[1]} ${ampm}`;
};

const getLatestAttendanceWithTime = (emp) => {
  const list = Array.isArray(emp?.attendance) ? emp.attendance : [];
  for (let i = list.length - 1; i >= 0; i--) {
    if (list[i]?.check_in || list[i]?.check_out) {
      return list[i];
    }
  }
  return null;
};

const normalizeTimeForInput = (value, fallback) => {
  const raw = String(value || "").trim();
  if (!raw) return fallback;

  const hhmmMatch = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hhmmMatch) {
    const hh = Number(hhmmMatch[1]);
    const mm = Number(hhmmMatch[2]);
    if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) {
      return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    }
  }

  const twelveHourMatch = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (twelveHourMatch) {
    let hh = Number(twelveHourMatch[1]);
    const mm = Number(twelveHourMatch[2]);
    const meridiem = twelveHourMatch[3].toUpperCase();
    if (hh >= 1 && hh <= 12 && mm >= 0 && mm <= 59) {
      if (meridiem === "AM" && hh === 12) hh = 0;
      if (meridiem === "PM" && hh !== 12) hh += 12;
      return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    }
  }

  return fallback;
};

function Attendance() {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [attendanceData, setAttendanceData] = useState([]);
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAtt, setSelectedAtt] = useState(null);

  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidayDate, setHolidayDate] = useState("");
  const [holidayTitle, setHolidayTitle] = useState("");
  const [holidayDescription, setHolidayDescription] = useState("");

  const [timingModal, setTimingModal] = useState(false);
  const [selectedEmpForTiming, setSelectedEmpForTiming] = useState(null);
  const [timing, setTiming] = useState({
    officeStart: "",
    officeEnd: "",
    graceMinutes: "",
    dailyWorkingHours: ""
  });
  const [nowTick, setNowTick] = useState(Date.now());

  const daysInMonth = useMemo(
    () => new Date(selectedYear, selectedMonth, 0).getDate(),
    [selectedYear, selectedMonth]
  );

  const fetchMonthlyData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/getMonthlyAttandenceByAdmin`, {
        params: { month: selectedMonth, year: selectedYear }
      });
      setAttendanceData(res.data || []);
    } catch (err) {
      console.error("Monthly attendance fetch error:", err);
      setAttendanceData([]);
    }
  };

  useEffect(() => {
    fetchMonthlyData();

    const interval = setInterval(() => {
      fetchMonthlyData();
    }, 15000); // keep login/exit/active time near real-time on admin screen

    return () => clearInterval(interval);
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    const timer = setInterval(() => {
      setNowTick(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getIcon = (status) => {
    if (!status) return "-";
    const s = String(status).toLowerCase();

    if (s === "present") return "P";
    if (s === "absent") return "A";
    if (s === "holiday") return "H";
    if (s === "paid leave" || s === "unpaid leave") return "L";
    if (s.includes("half")) return "HD";

    return "-";
  };

  const filteredEmployees = useMemo(
    () =>
      attendanceData.filter((emp) =>
        (emp.ename || "").toLowerCase().includes(search.toLowerCase())
      ),
    [attendanceData, search]
  );

  const todayDateKey = useMemo(() => toDateKey(new Date()), [nowTick]);

  const openDetailsModal = (attWithName) => {
    setSelectedAtt(attWithName);
    setModalOpen(true);
  };

  const closeDetailsModal = () => {
    setSelectedAtt(null);
    setModalOpen(false);
  };

  const openEditTiming = (emp) => {
    setSelectedEmpForTiming(emp);

    setTiming({
      officeStart: normalizeTimeForInput(emp.officeStart, "09:30"),
      officeEnd: normalizeTimeForInput(emp.officeEnd, "18:30"),
      graceMinutes: emp.graceMinutes ?? 10,
      dailyWorkingHours: emp.dailyWorkingHours ?? 9
    });

    setTimingModal(true);
  };

  const closeTimingModal = () => {
    setSelectedEmpForTiming(null);
    setTimingModal(false);
  };

  const saveTiming = async () => {
    if (!selectedEmpForTiming) return;

    const { officeStart, officeEnd, graceMinutes, dailyWorkingHours } = timing;

    if (!officeStart || !officeEnd || !graceMinutes || !dailyWorkingHours) {
      alert("All timing fields are required.");
      return;
    }

    if (isNaN(graceMinutes) || graceMinutes < 0) {
      alert("Grace minutes must be a non-negative number.");
      return;
    }

    if (isNaN(dailyWorkingHours) || dailyWorkingHours <= 0) {
      alert("Daily working hours must be a positive number.");
      return;
    }

    try {
      await axios.put(
        `${API_URL}/api/updateOfficeTiming/${selectedEmpForTiming.empId}`,
        timing
      );

      alert("Office timing updated successfully!");
      setTimingModal(false);
      fetchMonthlyData();
    } catch (error) {
      console.error("Update timing error:", error);
      alert(error.response?.data?.message || "Failed to update timing");
    }
  };

  const addHoliday = async () => {
    if (!holidayDate || !holidayTitle.trim()) {
      alert("Please select date and title");
      return;
    }

    try {
      await axios.post(`${API_URL}/api/holiday`, {
        date: holidayDate,
        title: holidayTitle,
        description: holidayDescription,
        isPaid: true
      });

      alert("Holiday added successfully!");

      setShowHolidayModal(false);
      setHolidayDate("");
      setHolidayTitle("");
      setHolidayDescription("");
      fetchMonthlyData();
    } catch (error) {
      console.error("Add holiday error:", error);
      alert(error.response?.data?.message || "Error adding holiday");
    }
  };

  const exportToExcel = () => {
    if (!attendanceData || attendanceData.length === 0) {
      alert("No data to export");
      return;
    }

    const header = ["Employee"];
    for (let d = 1; d <= daysInMonth; d++) header.push(String(d));

    const rows = attendanceData.map((emp) => {
      const row = [emp.ename || ""];

      for (let d = 0; d < daysInMonth; d++) {
        const att = emp.attendance?.[d];
        row.push(att ? att.status || "" : "");
      }
      return row;
    });

    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");

    XLSX.writeFile(
      wb,
      `Attendance_${months[selectedMonth - 1]}_${selectedYear}.xlsx`
    );
  };

  return (
    <>
      <div className="att-page">
        <div className="att-shell">
          <div className="att-hero">
            <div>
              <p className="att-eyebrow">Workforce Control</p>
              <h3>Admin Monthly Attendance</h3>
              <span>Track daily status, office timing, leaves, and late logs</span>
            </div>
          </div>

          <div className="att-toolbar">
            <select
              className="form-select att-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
            >
              {months.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>

            <select
              className="form-select att-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(
                (y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                )
              )}
            </select>

            <input
              type="text"
              className="form-control att-search"
              placeholder="Search Employee..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <button className="btn att-btn light" onClick={() => setShowHolidayModal(true)}>
              + Add Holiday
            </button>

            <button className="btn att-btn primary" onClick={exportToExcel}>
              Export Excel
            </button>
          </div>

          <div className="att-legend">
            <strong>Note:</strong>
            <span>H-Holiday</span>
            <span>P-Present</span>
            <span>A-Absent</span>
            <span>L-Leave</span>
            <span>HD-Half Day</span>
            <span className="late">Red Dot-Late Arrival</span>
          </div>

          <div className="att-main-card">
            {filteredEmployees.length === 0 && (
              <div className="text-muted text-center py-4">
                No employees found for this month/year or search.
              </div>
            )}

            {filteredEmployees.map((emp) => (
              <div key={emp.empId} className="att-emp-card">
                {(() => {
                  const todaysAtt = emp.attendance?.find((a) => a?.date === todayDateKey);
                  const latestAtt = getLatestAttendanceWithTime(emp);
                  const timingSource = todaysAtt || latestAtt;

                  const checkInDt = parseDateAndTime(timingSource?.date, timingSource?.check_in);
                  const checkOutDt = parseDateAndTime(timingSource?.date, timingSource?.check_out);

                  const activeMs = checkInDt
                    ? (checkOutDt ? checkOutDt.getTime() - checkInDt.getTime() : nowTick - checkInDt.getTime())
                    : null;

                  return (
                    <>
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="fw-bold mb-0">{emp.ename}</h5>

                        <button
                          className="btn btn-sm att-btn ghost"
                          onClick={() => openEditTiming(emp)}
                        >
                          Edit Timing
                        </button>
                      </div>

                      <p className="att-meta">
                        Office: {emp.officeStart || "09:30"} to {emp.officeEnd || "18:30"}
                        <span>|</span> Grace: {emp.graceMinutes ?? 10}m
                        <span>|</span> Hours: {emp.dailyWorkingHours ?? 9}
                      </p>

                      <p className="att-live-meta">
                        Login: {formatTime12H(timingSource?.check_in)}
                        <span>|</span> Exit: {formatTime12H(timingSource?.check_out)}
                        <span>|</span> Admin Recorded Hours: {timingSource?.workingHours != null ? timingSource.workingHours.toFixed(2) : "0.00"} hr
                      </p>

                      {emp.summary && (
                        <div className="att-summary">
                          <b>Summary:</b>
                          <span>Present: <em>{emp.summary.present || 0}</em></span>
                          <span>Absent: <em>{emp.summary.absent || 0}</em></span>
                          <span>Half Day: <em>{emp.summary.halfday || 0}</em></span>
                          <span>Holiday: <em>{emp.summary.holiday || 0}</em></span>
                          <span>Paid Leave: <em>{emp.summary.paidLeaves || 0}</em></span>
                          <span>Unpaid Leave: <em>{emp.summary.unpaidLeaves || 0}</em></span>
                          <span>Late: <em>{emp.summary.lateCount || 0} days</em></span>
                          <span>Late Hours: <em>{emp.summary.lateHours || 0} hr</em></span>
                        </div>
                      )}

                      <div className="d-flex fw-bold mb-2">
                        <div style={{ width: "150px" }}>Date</div>
                        <div className="att-days-row" style={{ minWidth: `${daysInMonth * 35}px` }}>
                          {Array.from({ length: daysInMonth }).map((_, i) => (
                            <div key={i + 1} className="att-day-head">
                              {i + 1}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="d-flex align-items-center">
                        <div style={{ width: "150px", fontWeight: 600 }}>Status</div>

                        <div className="att-days-row" style={{ minWidth: `${daysInMonth * 35}px` }}>
                          {Array.from({ length: daysInMonth }).map((_, index) => {
                            const att = emp.attendance?.[index];

                            return (
                              <div
                                key={index}
                                onClick={() => att && openDetailsModal({ ...att, ename: emp.ename })}
                                className="att-day-cell"
                                title={
                                  att?.isLateMinutes > 0
                                    ? `Late by ${att.isLateMinutes} minutes`
                                    : att?.status || ""
                                }
                              >
                                {att ? getIcon(att.status) : "-"}

                                {att?.isLateMinutes > 0 && <span className="att-late-dot" />}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
      </div>

      {modalOpen && selectedAtt && (
        <div className="att-side-overlay">
          <div className="att-side-panel">
            <h4>{selectedAtt.ename} - Details</h4>
            <hr />
            <p><b>Date:</b> {selectedAtt.date}</p>
            <p><b>Status:</b> {selectedAtt.status}</p>
            <p><b>Check-in:</b> {formatTime12H(selectedAtt.check_in)}</p>
            <p><b>Check-out:</b> {formatTime12H(selectedAtt.check_out)}</p>
            <p><b>Working Hours:</b> {selectedAtt.workingHours != null ? formatWorkingHours(selectedAtt.workingHours) : "--"}</p>
            <p><b>Total Break Duration:</b> {formatBreakDuration(selectedAtt.totalBreakDuration)}</p>
            {selectedAtt.isLateMinutes > 0 && <p><b>Late:</b> {selectedAtt.isLateMinutes} minutes</p>}

            {selectedAtt.breaks && selectedAtt.breaks.length > 0 && (
              <div className="mt-3" style={{ maxHeight: "200px", overflowY: "auto" }}>
                <h6>Break Sessions:</h6>
                <ul style={{ paddingLeft: "15px", fontSize: "13px" }}>
                  {selectedAtt.breaks.map((brk, idx) => (
                    <li key={idx} style={{ marginBottom: "4px" }}>
                      <strong>{idx + 1}:</strong> {brk.start} - {brk.end} ({brk.duration} min)
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button className="btn att-btn danger mt-3" onClick={closeDetailsModal}>
              Close
            </button>
          </div>
        </div>
      )}

      {timingModal && (
        <div className="att-side-overlay">
          <div className="att-side-panel">
            <h4>Edit Office Timing</h4>
            <hr />

            <label>Office Start</label>
            <input
              type="time"
              step="60"
              className="form-control mb-2"
              value={timing.officeStart}
              onChange={(e) => setTiming({ ...timing, officeStart: e.target.value })}
            />

            <label>Office End</label>
            <input
              type="time"
              step="60"
              className="form-control mb-2"
              value={timing.officeEnd}
              onChange={(e) => setTiming({ ...timing, officeEnd: e.target.value })}
            />

            <label>Grace Minutes</label>
            <input
              type="number"
              className="form-control mb-2"
              value={timing.graceMinutes}
              onChange={(e) => setTiming({ ...timing, graceMinutes: e.target.value })}
            />

            <label>Daily Working Hours</label>
            <input
              type="number"
              className="form-control mb-3"
              value={timing.dailyWorkingHours}
              onChange={(e) => setTiming({ ...timing, dailyWorkingHours: e.target.value })}
            />

            <button className="btn att-btn primary w-100 mb-2" onClick={saveTiming}>
              Save Timing
            </button>

            <button className="btn att-btn neutral w-100" onClick={closeTimingModal}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showHolidayModal && (
        <div className="att-center-overlay">
          <div className="att-center-modal">
            <h4 className="mb-3">Add Holiday</h4>

            <label>Date</label>
            <input
              type="date"
              className="form-control mb-2"
              value={holidayDate}
              onChange={(e) => setHolidayDate(e.target.value)}
            />

            <label>Holiday Title</label>
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Example: Diwali, Christmas"
              value={holidayTitle}
              onChange={(e) => setHolidayTitle(e.target.value)}
            />

            <label>Description (Optional)</label>
            <textarea
              className="form-control mb-3"
              rows="3"
              value={holidayDescription}
              onChange={(e) => setHolidayDescription(e.target.value)}
            />

            <button className="btn att-btn primary w-100" onClick={addHoliday}>
              Add Holiday
            </button>

            <button className="btn att-btn neutral w-100 mt-2" onClick={() => setShowHolidayModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default Attendance;
